import type {Theme} from '@mui/material/styles'
import {lighten} from '@mui/material/styles'

// Per-signal line styling for the /signals workspace (AB#5520): color,
// width, dash, interpolation, sample points (size + shape, AB#5690), area
// fill, points-only (line hidden). Rides the URL (`st` param, non-default
// entries only) and saved views. Encoding: csv of
// `id:rrggbb:width:dash:curve:pts:fill:nl:tl:op:states:place:ptsize:ptshape:ptcolor:nth:nm:na:tr:trc:trw:trd:pl`
// with empty slots for unset fields — older shorter entries parse fine.

export type LineDash = 'solid' | 'dashed' | 'dotted'
export type PointShape = 'circle' | 'square' | 'triangle' | 'diamond'
// 'auto' (absent) = linear for numerics, stepped for categoricals.
export type LineCurve = 'linear' | 'stepped' | 'smooth'

export interface SignalStyle {
  color?: string // '#rrggbb'
  width?: number
  dash?: LineDash
  curve?: LineCurve
  points?: boolean
  // Marker radius in px; undefined = derived from the line width so points
  // scale with the line (defaultPointRadius).
  pointSize?: number
  // Marker shape; undefined = circle.
  pointShape?: PointShape
  // Marker fill ('#rrggbb'); undefined = the line color.
  pointColor?: string
  fill?: boolean
  // Hide the line, show only sample points (markers force-on).
  noLine?: boolean
  // Categorical only: render as a horizontal state-timeline band (colored
  // segments per state run) instead of a stepped line.
  timeline?: boolean
  // State-timeline band opacity (0–1); undefined = fully opaque.
  timelineOpacity?: number
  // Per-state color overrides for the timeline, keyed by the state value's
  // string form ('#rrggbb'); states without an override use the palette.
  stateColors?: Record<string, string>
  // Where the timeline sits. undefined = in the plot ("background": stacked
  // lanes behind the lines, opacity-adjustable). 'top' = a strip in the margin
  // above the plot; 'bottom' = a strip below it.
  timelinePlacement?: 'top' | 'bottom'
  // Hide THIS signal's threshold shading + violation marks (AB#5928).
  // undefined = shown; per-signal so a merged chart can mix.
  noThresholds?: boolean
  // Hide the anomaly detection model's expected-range band on this signal's
  // overlay chart (AB#5931 phase 2). undefined = shown when a model is active.
  noModel?: boolean
  // Show THIS signal's anomaly shades (time view and overlay, AB#5931).
  // Opt-in since AB#6763: undefined = hidden AND not fetched, so a view with
  // many signals does not fire one anomaly request per signal on load.
  anomalies?: boolean
  // Draw a least-squares trend line over the plotted samples (AB#6071).
  // Numeric signals only; the fit follows the visible domain.
  trend?: boolean
  // Trend line appearance. undefined = the signal's (or, in the overlay, the
  // run's) own line colour, DEFAULT_TREND_WIDTH, and DEFAULT_TREND_DASH — so
  // the trend reads as a companion of its line, not a second series.
  trendColor?: string // '#rrggbb'
  trendWidth?: number
  trendDash?: LineDash
  // Print each drawn sample marker's value next to it (AB#6607). Only takes
  // effect while markers are visible (points or points-only) — the flag may
  // stay stored when points are switched off, but then nothing draws.
  pointLabels?: boolean
}

// Value labels render only on visible markers (AB#6607).
export const showsPointLabels = (style: SignalStyle | undefined): boolean =>
  Boolean(style?.pointLabels) && (Boolean(style?.points) || Boolean(style?.noLine))

// Default line color per scheme: the light theme's wine red reads well on
// white, but on the dark background the brand orange is too muted — lift its
// luminance so single-series charts stay crisp.
export const defaultLineColor = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.primary.main, 0.3)
    : theme.palette.primary.main

export const DEFAULT_LINE_WIDTH = 2
export const LINE_WIDTH_OPTIONS = [1, 1.5, 2, 3, 4]
// The trend is a reading aid, so it defaults thinner and dashed — a solid
// same-width line competes with the data it describes (AB#6071).
export const DEFAULT_TREND_WIDTH = 1.5
export const DEFAULT_TREND_DASH: LineDash = 'dashed'
export const POINT_SIZE_OPTIONS = [2, 3, 4, 6, 8]
export const POINT_SHAPES: PointShape[] = ['circle', 'square', 'triangle', 'diamond']

// Unless overridden, markers ride the line width (a 4px line swallowed the
// old fixed r=2 dots) — same derivation the overlay chart always used.
export const defaultPointRadius = (width?: number): number =>
  Math.max(2, (width ?? DEFAULT_LINE_WIDTH) + 1)

// Tableau10 (the overlay palette) plus a few high-contrast extras.
export const COLOR_SWATCHES = [
  '#4e79a7',
  '#f28e2c',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#edc949',
  '#af7aa1',
  '#ff9da7',
  '#9c755f',
  '#bab0ab',
  '#1f77b4',
  '#d62728',
  '#2ca02c',
  '#9467bd',
]

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const HEX_PATTERN = /^[0-9a-f]{6}$/i
const DASHES: LineDash[] = ['solid', 'dashed', 'dotted']
const CURVES: LineCurve[] = ['linear', 'stepped', 'smooth']

export const strokeDasharray = (dash: LineDash | undefined): string | undefined =>
  dash === 'dashed' ? '6 4' : dash === 'dotted' ? '2 3' : undefined

export const parseSignalStyles = (params: URLSearchParams): Record<string, SignalStyle> => {
  const raw = params.get('st')
  if (!raw) {
    return {}
  }
  const styles: Record<string, SignalStyle> = {}
  for (const entry of raw.split(',')) {
    const [
      id,
      color,
      width,
      dash,
      curve,
      points,
      fill,
      noLine,
      timeline,
      opacity,
      stateColorsRaw,
      placement,
      pointSize,
      pointShape,
      pointColor,
      noThresholds,
      noModel,
      anomalies,
      trend,
      trendColor,
      trendWidth,
      trendDash,
      pointLabels,
    ] = entry.split(':')
    if (!UUID_PATTERN.test(id)) {
      continue
    }
    const style: SignalStyle = {}
    if (color && HEX_PATTERN.test(color)) {
      style.color = `#${color.toLowerCase()}`
    }
    const parsedWidth = Number(width)
    if (width && Number.isFinite(parsedWidth) && parsedWidth >= 0.5 && parsedWidth <= 6) {
      style.width = parsedWidth
    }
    if (DASHES.includes(dash as LineDash) && dash !== 'solid') {
      style.dash = dash as LineDash
    }
    if (CURVES.includes(curve as LineCurve)) {
      style.curve = curve as LineCurve
    }
    if (points === '1') {
      style.points = true
    }
    if (fill === '1') {
      style.fill = true
    }
    if (noLine === '1') {
      style.noLine = true
    }
    if (timeline === '1') {
      style.timeline = true
    }
    const parsedOpacity = Number(opacity)
    if (opacity && Number.isFinite(parsedOpacity) && parsedOpacity >= 0 && parsedOpacity <= 1) {
      style.timelineOpacity = parsedOpacity
    }
    if (stateColorsRaw) {
      // 'encKey=hex;encKey=hex' — keys are encodeURIComponent'd so state
      // labels with separators survive the ':'/','-delimited st format.
      const stateColors: Record<string, string> = {}
      for (const pair of stateColorsRaw.split(';')) {
        const eq = pair.indexOf('=')
        if (eq <= 0) {
          continue
        }
        const hex = pair.slice(eq + 1)
        if (HEX_PATTERN.test(hex)) {
          try {
            stateColors[decodeURIComponent(pair.slice(0, eq))] = `#${hex.toLowerCase()}`
          } catch {
            // A malformed % escape in a hand-edited `st` param makes
            // decodeURIComponent throw a URIError; skip this pair and keep
            // parsing the rest instead of crashing the whole style parse.
            continue
          }
        }
      }
      if (Object.keys(stateColors).length > 0) {
        style.stateColors = stateColors
      }
    }
    // 'top'/'bot' are strips; legacy 'bg' (a former full-height mode) now maps
    // to the default in-plot placement, i.e. no override.
    if (placement === 'top') {
      style.timelinePlacement = 'top'
    } else if (placement === 'bot') {
      style.timelinePlacement = 'bottom'
    }
    const parsedPointSize = Number(pointSize)
    if (
      pointSize &&
      Number.isFinite(parsedPointSize) &&
      parsedPointSize >= 1 &&
      parsedPointSize <= 12
    ) {
      style.pointSize = parsedPointSize
    }
    if (POINT_SHAPES.includes(pointShape as PointShape) && pointShape !== 'circle') {
      style.pointShape = pointShape as PointShape
    }
    if (pointColor && HEX_PATTERN.test(pointColor)) {
      style.pointColor = `#${pointColor.toLowerCase()}`
    }
    if (noThresholds === '1') {
      style.noThresholds = true
    }
    if (noModel === '1') {
      style.noModel = true
    }
    // The `na` slot: 's' = shown (AB#6763). Empty, and the legacy '1' that
    // used to mean "hidden", both read as the hidden default now.
    if (anomalies === 's') {
      style.anomalies = true
    }
    if (trend === '1') {
      style.trend = true
    }
    if (trendColor && HEX_PATTERN.test(trendColor)) {
      style.trendColor = `#${trendColor.toLowerCase()}`
    }
    const parsedTrendWidth = Number(trendWidth)
    if (
      trendWidth &&
      Number.isFinite(parsedTrendWidth) &&
      parsedTrendWidth >= 0.5 &&
      parsedTrendWidth <= 6
    ) {
      style.trendWidth = parsedTrendWidth
    }
    // Unlike the data line, 'solid' is a real OVERRIDE here (the trend's own
    // default is dashed), so all three values encode.
    if (DASHES.includes(trendDash as LineDash)) {
      style.trendDash = trendDash as LineDash
    }
    if (pointLabels === '1') {
      style.pointLabels = true
    }
    if (Object.keys(style).length > 0) {
      styles[id] = style
    }
  }
  return styles
}

export const writeSignalStyles = (
  params: URLSearchParams,
  styles: Record<string, SignalStyle>,
): void => {
  const entries = Object.entries(styles).filter(([, style]) => Object.keys(style).length > 0)
  if (entries.length) {
    params.set(
      'st',
      entries
        .map(([id, style]) => {
          const slots = [
            style.color ? style.color.slice(1) : '',
            style.width ?? '',
            style.dash ?? '',
            style.curve ?? '',
            style.points ? '1' : '',
            style.fill ? '1' : '',
            style.noLine ? '1' : '',
            style.timeline ? '1' : '',
            style.timelineOpacity ?? '',
            style.stateColors && Object.keys(style.stateColors).length
              ? Object.entries(style.stateColors)
                  .map(([value, hex]) => `${encodeURIComponent(value)}=${hex.slice(1)}`)
                  .join(';')
              : '',
            style.timelinePlacement === 'top'
              ? 'top'
              : style.timelinePlacement === 'bottom'
                ? 'bot'
                : '',
            style.pointSize ?? '',
            style.pointShape ?? '',
            style.pointColor ? style.pointColor.slice(1) : '',
            style.noThresholds ? '1' : '',
            style.noModel ? '1' : '',
            style.anomalies ? 's' : '',
            style.trend ? '1' : '',
            style.trendColor ? style.trendColor.slice(1) : '',
            style.trendWidth ?? '',
            style.trendDash ?? '',
            style.pointLabels ? '1' : '',
          ]
          // Trailing empty slots stay off the URL.
          while (slots.length && slots[slots.length - 1] === '') {
            slots.pop()
          }
          return [id, ...slots].join(':')
        })
        .join(','),
    )
  } else {
    params.delete('st')
  }
}
